import type { TranslationKey } from "@privacy/translations";
import type { OptionCategoryType } from "../api/optionCategories";
import type { ConnectedEntityFormType, OptionCategoryListTextKeys } from "./types";

export type OptionCategoryScopeName = "organisation" | "customer";

type OptionCategoryTypeConfig = {
  textKeys: Omit<OptionCategoryListTextKeys, "subTitle">;
  subTitle: Record<OptionCategoryScopeName, TranslationKey>;
  usageFormTypes: Array<ConnectedEntityFormType>;
  listTabKey: TranslationKey;
  historyNameKey: TranslationKey;
};

export const OPTION_CATEGORY_TYPES: Array<OptionCategoryType> = [
  "categoriesOfProcessings",
  "categoriesOfPersonalData",
  "categoriesOfDataSubjects",
  "projects",
  "technicalSecurityMeasurements",
];

const GENERIC_CATEGORY_TEXT_KEYS = {
  alreadyExistsError: "OptionCategoryDrawer.Create.Category.OptionAlreadyExistError",
  deleteHeader: "OptionCategoryDrawer.Delete.Category.ConfirmHeader",
  deleteButton: "OptionCategoryDrawer.Delete.Category.ConfirmDelete",
  empty: "OptionCategoryGrid.NoRows",
} satisfies Partial<OptionCategoryListTextKeys>;

export const OPTION_CATEGORY_TYPE_CONFIG: Record<OptionCategoryType, OptionCategoryTypeConfig> = {
  categoriesOfProcessings: {
    textKeys: {
      ...GENERIC_CATEGORY_TEXT_KEYS,
      title: "Organisation.CategoriesOfProcessings",
      createButton: "OptionCategoryList.CategoriesOfProcessings.CreateButton",
      createTitle: "OptionCategoryList.CategoriesOfProcessings.CreateTitle",
      editTitle: "OptionCategoryList.CategoriesOfProcessings.EditTitle",
      nameLabel: "OptionCategoryList.CategoriesOfProcessings.NameLabel",
      namePlaceholder: "OptionCategoryList.CategoriesOfProcessings.NamePlaceholder",
      descriptionPlaceholder: "OptionCategoryList.CategoriesOfProcessings.DescriptionPlaceholder",
      inUseTooltip: "OptionCategoryList.CategoriesOfProcessings.InUseTooltip",
      inUseWithoutAccessTooltip: "OptionCategoryList.CategoriesOfProcessings.InUseWithoutAccessTooltip",
      lockedTooltip: "OptionCategoryDrawer.Tooltip.CategoriesOfProcessings",
    },
    subTitle: {
      organisation: "Organisation.CategoriesOfProcessings.SubTitle",
      customer: "EnterpriseCustomer.CategoriesOfProcessings.SubTitle",
    },
    usageFormTypes: ["REGISTRATIONS", "PRIVACY_PROCESSOR_REGISTRATIONS"],
    listTabKey: "OptionCategoryList.CategoriesOfProcessings.ListTab",
    historyNameKey: "OptionCategoryList.CategoriesOfProcessings.HistoryName",
  },
  categoriesOfPersonalData: {
    textKeys: {
      ...GENERIC_CATEGORY_TEXT_KEYS,
      title: "Organisation.CategoriesOfPersonalData",
      createButton: "OptionCategoryGrid.CategoriesOfPersonalData.NewOption",
      createTitle: "OptionCategoryGrid.CategoriesOfPersonalData.AddOption",
      editTitle: "OptionCategoryList.CategoriesOfPersonalData.EditTitle",
      nameLabel: "OptionCategoryGrid.CategoriesOfPersonalData.NameOfOption",
      namePlaceholder: "OptionCategoryGrid.CategoriesOfPersonalData.AddOptionPlaceholder",
      descriptionPlaceholder: "OptionCategoryList.CategoriesOfPersonalData.DescriptionPlaceholder",
      inUseTooltip: "OptionCategoryList.CategoriesOfPersonalData.InUseTooltip",
      inUseWithoutAccessTooltip: "OptionCategoryList.CategoriesOfPersonalData.InUseWithoutAccessTooltip",
      lockedTooltip: "OptionCategoryDrawer.Tooltip.CategoriesOfPersonalData",
    },
    subTitle: {
      organisation: "Organisation.CategoriesOfPersonalData.SubTitle",
      customer: "EnterpriseCustomer.CategoriesOfPersonalData.SubTitle",
    },
    usageFormTypes: ["REGISTRATIONS"],
    listTabKey: "OptionCategoryList.CategoriesOfPersonalData.ListTab",
    historyNameKey: "OptionCategoryList.CategoriesOfPersonalData.HistoryName",
  },
  categoriesOfDataSubjects: {
    textKeys: {
      ...GENERIC_CATEGORY_TEXT_KEYS,
      title: "Organisation.CategoriesOfDataSubjects",
      createButton: "OptionCategoryGrid.CategoriesOfDataSubjects.NewOption",
      createTitle: "OptionCategoryGrid.CategoriesOfDataSubjects.AddOption",
      editTitle: "OptionCategoryList.CategoriesOfDataSubjects.EditTitle",
      nameLabel: "OptionCategoryGrid.CategoriesOfDataSubjects.NameOfOption",
      namePlaceholder: "OptionCategoryGrid.CategoriesOfDataSubjects.AddOptionPlaceholder",
      descriptionPlaceholder: "OptionCategoryList.CategoriesOfDataSubjects.DescriptionPlaceholder",
      inUseTooltip: "OptionCategoryList.CategoriesOfDataSubjects.InUseTooltip",
      inUseWithoutAccessTooltip: "OptionCategoryList.CategoriesOfDataSubjects.InUseWithoutAccessTooltip",
      lockedTooltip: "OptionCategoryDrawer.Tooltip.CategoriesOfDataSubjects",
    },
    subTitle: {
      organisation: "Organisation.CategoriesOfDataSubjects.SubTitle",
      customer: "EnterpriseCustomer.CategoriesOfDataSubjects.SubTitle",
    },
    usageFormTypes: ["REGISTRATIONS"],
    listTabKey: "OptionCategoryList.CategoriesOfDataSubjects.ListTab",
    historyNameKey: "OptionCategoryList.CategoriesOfDataSubjects.HistoryName",
  },
  projects: {
    textKeys: {
      title: "Organisation.Projects",
      createButton: "OptionCategoryGrid.Projects.NewOption",
      createTitle: "OptionCategoryGrid.Projects.AddOption",
      editTitle: "OptionCategoryList.Projects.EditTitle",
      nameLabel: "OptionCategoryGrid.Projects.NameOfOption",
      namePlaceholder: "OptionCategoryGrid.Projects.AddOptionPlaceholder",
      descriptionPlaceholder: "OptionCategoryGrid.Projects.DescriptionPlaceholder",
      alreadyExistsError: "OptionCategoryDrawer.Create.Projects.OptionAlreadyExistError",
      deleteHeader: "OptionCategoryDrawer.Delete.Projects.ConfirmHeader",
      deleteButton: "OptionCategoryDrawer.Delete.Projects.ConfirmDelete",
      inUseTooltip: "OptionCategoryList.Projects.InUseTooltip",
      inUseWithoutAccessTooltip: "OptionCategoryList.Projects.InUseWithoutAccessTooltip",
      lockedTooltip: "OptionCategoryDrawer.Tooltip.Projects",
      empty: "OptionCategoryGrid.Projects.NoRows",
    },
    subTitle: {
      organisation: "Organisation.Projects.SubTitle",
      customer: "EnterpriseCustomer.Projects.SubTitle",
    },
    usageFormTypes: ["REGISTRATIONS"],
    listTabKey: "OptionCategoryList.Projects.ListTab",
    historyNameKey: "OptionCategoryList.Projects.HistoryName",
  },
  technicalSecurityMeasurements: {
    textKeys: {
      title: "Organisation.TechnicalSecurityMeasurements",
      createButton: "OptionCategoryGrid.TechnicalSecurityMeasurements.NewOption",
      createTitle: "OptionCategoryGrid.TechnicalSecurityMeasurements.AddOption",
      editTitle: "OptionCategoryList.TechnicalSecurityMeasurements.EditTitle",
      nameLabel: "OptionCategoryGrid.TechnicalSecurityMeasurements.NameOfOption",
      namePlaceholder: "OptionCategoryGrid.TechnicalSecurityMeasurements.AddOptionPlaceholder",
      descriptionPlaceholder: "OptionCategoryGrid.TechnicalSecurityMeasurements.DescriptionPlaceholder",
      alreadyExistsError: "OptionCategoryDrawer.Create.TechnicalSecurityMeasurements.OptionAlreadyExistError",
      deleteHeader: "OptionCategoryDrawer.Delete.TechnicalSecurityMeasurements.ConfirmHeader",
      deleteButton: "OptionCategoryDrawer.Delete.TechnicalSecurityMeasurements.ConfirmDelete",
      inUseTooltip: "OptionCategoryList.TechnicalSecurityMeasurements.InUseTooltip",
      inUseWithoutAccessTooltip: "OptionCategoryList.TechnicalSecurityMeasurements.InUseWithoutAccessTooltip",
      lockedTooltip: "OptionCategoryDrawer.Tooltip.TechnicalSecurityMeasurements",
      empty: "OptionCategoryGrid.TechnicalSecurityMeasurements.NoRows",
    },
    subTitle: {
      organisation: "Organisation.TechnicalSecurityMeasurements.SubTitle",
      customer: "EnterpriseCustomer.TechnicalSecurityMeasurements.SubTitle",
    },
    usageFormTypes: ["SYSTEMS"],
    listTabKey: "OptionCategoryList.TechnicalSecurityMeasurements.ListTab",
    historyNameKey: "OptionCategoryList.TechnicalSecurityMeasurements.HistoryName",
  },
};

export const optionCategoryTextKeys = (
  categoryType: OptionCategoryType,
  scopeName: OptionCategoryScopeName
): OptionCategoryListTextKeys => {
  const config = OPTION_CATEGORY_TYPE_CONFIG[categoryType];

  return { ...config.textKeys, subTitle: config.subTitle[scopeName] };
};
